import { reject, isNil, concat } from 'ramda';

export enum SearchTypeValues {
  None = '',
  MerchantName = 'merchantName',
  PostalCode = 'postalCode',
  AddressSearchString = 'addressSearchString',
  CardNetworkId = 'cardNetworkId',
  Vmid = 'VMID',
}

export enum VenueTypeValues {
  Online = 'online',
  Nearby = 'nearby',
  All = '',
}

export type Values = {
  searchText?: string;
  searchType?: SearchTypeValues;
  filter?: string;
  cnidsToFilter?: string;
  venueType?: VenueTypeValues;
};

export type SearchDropDownOption = {
  key: string;
  text: string;
  value: SearchTypeValues | null;
};

type Errors = {
  searchText?: string;
  searchType?: string;
  filter?: string;
  cnidsToFilter?: string;
};

const searchTypes = {
  options: [
    {
      key: 'none',
      text: 'None',
      value: SearchTypeValues.None,
    },
    {
      key: 'zip',
      text: 'Zip Code',
      value: SearchTypeValues.PostalCode,
    },
    {
      key: 'address',
      text: 'Address',
      value: SearchTypeValues.AddressSearchString,
    },
  ],
  homeExtraOptions: [
    {
      key: 'name',
      text: 'Merchant Name',
      value: SearchTypeValues.MerchantName,
    },
  ],
  internalOptions: [
    {
      key: 'cnid',
      text: 'CNID',
      value: SearchTypeValues.CardNetworkId,
    },
  ],
  label: {
    children: 'Search By',
  },
  placeholder: 'Search Type',
  venueDiscoveryOptions: [
    {
      key: 'vmid',
      text: 'VMID',
      value: SearchTypeValues.Vmid,
    },
  ],
};

const filterTypes = {
  options: [
    {
      key: 'filters',
      text: 'None',
      value: null,
    },
    {
      key: 'missingCNIDs',
      text: 'Missing CNIDs',
      value: 'missingCnidQuery',
    },
    {
      key: 'noTransactions',
      text: 'No Transactions',
      value: 'missingTransactionsQuery',
    },
  ],
  label: {
    children: 'Filter By',
  },
  placeholder: 'Filter By',
};

const venueTypes = {
  options: [
    {
      key: VenueTypeValues.All,
      text: 'All',
      value: VenueTypeValues.All,
    },
    {
      key: VenueTypeValues.Online,
      text: 'Online',
      value: VenueTypeValues.Online,
    },
    {
      key: VenueTypeValues.Nearby,
      text: 'Nearby',
      value: VenueTypeValues.Nearby,
    },
  ],
  label: {
    children: 'Venue Type',
  },
  placeholder: 'Venue Type',
};

const cardNetworksTypes = {
  options: [
    {
      key: 'cnids',
      text: 'None',
      value: null,
    },
    {
      key: 'all',
      text: 'All Card Networks',
      value: 'ALL',
    },
    {
      key: 'visa',
      text: 'Visa',
      value: 'VISA',
    },
    {
      key: 'mastercard',
      text: 'Master Card',
      value: 'MASTERCARD',
    },
    {
      key: 'amex',
      text: 'AMEX',
      value: 'AMEX',
    },
    {
      key: 'any',
      text: 'Any Card Network',
      value: 'ANY',
    },
  ],
  label: {
    children: 'Card Networks',
  },
  placeholder: 'Card Networks',
};

const searchDropDownOptions = (
  showVMID: boolean,
  showOnlyVMID: boolean,
  isInternalUser: boolean,
  isHomeView: boolean,
): Array<SearchDropDownOption> => {
  let options = searchTypes.options;

  if (isHomeView) {
    options = concat(options, searchTypes.homeExtraOptions);
  }

  if (isInternalUser) {
    options = concat(options, searchTypes.internalOptions);
  }

  if (showOnlyVMID) {
    return searchTypes.venueDiscoveryOptions;
  }

  return showVMID
    ? concat(options, searchTypes.venueDiscoveryOptions)
    : options;
};

const globalValidator = (showOnlyVMID: boolean) => ({
  searchText,
  searchType,
  cnidsToFilter,
  filter,
}: any) => {
  const errors: Errors = {};

  if (showOnlyVMID) {
    return;
  }

  if (!(searchText && searchType) && !(cnidsToFilter && filter)) {
    errors.filter = 'Please enter some search criteria or a filter';
  }

  if (cnidsToFilter && !filter) {
    errors.filter =
      'Please enter a filter to use with the card network selected';
  }

  return reject(isNil, errors);
};

const validators = {
  globalValidator,
};

export {
  searchTypes,
  validators,
  filterTypes,
  venueTypes,
  cardNetworksTypes,
  searchDropDownOptions,
};
